import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Sidebar } from 'Containers/shared/sidebar'
import { HeaderLabel } from 'Components/shared/sidebar'
import { ContractsContainer } from 'Containers/shared/contracts'
import {
  OnDemandFilter,
  OnDemandEditDuplicateFilter,
} from 'Containers/shared/segmentfilters'
import {
  Body,
  BodyTitle,
  BodyActions,
  BodyHeader,
  BodySubHeader,
} from 'Components/shared/body'
import { clearNewOnDemandSegment } from 'Containers/candidates/segment/NewOnDemandSegmentSlice'
import { DefaultButton } from 'Components/shared/buttons'
import { useLazyGetSegmentQuery } from 'Services/segments/segments'
import { useLazyGetCandidatesContractsByIdQuery } from 'Services/contracts/candidatesContracts'
import { OnDemandExploreSegmentsFilter } from 'Containers/shared/segmentfilters'
import { Permissions } from 'Configs/userPermissions'
import { useHasPermission } from 'Hooks/useHasPermission'
import { useToggle } from 'Hooks/useToggle'
import { Loading } from 'Utils/loading'
import {
  ContractSource,
  ContractTypeReadable,
  ContractStatus,
  SegmentAction,
} from 'Types/index'

export const Contracts = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { ipedId, segmentId } = useParams()
  const query = new URLSearchParams(useLocation().search)
  const segmentAction = query.get('action') as SegmentAction
  const { hasPermission: canAddSegment } = useHasPermission(
    Permissions.AddSegment
  )
  const { hasPermission: canCreateContract, isFetching } = useHasPermission(
    Permissions.CreateContract
  )

  const [showSidebar, setShowSidebar] = useToggle(false)
  const [sidebarTitle, setSidebarTitle] = useState(SegmentAction.new)

  const [fetchContract, { data: candidateContract }] =
    useLazyGetCandidatesContractsByIdQuery()

  const [fetchSegment, { data: segmentData, isLoading: isSegmentLoading, isFetching: isSegmentFetching }] = useLazyGetSegmentQuery()

  const handleCloseSidebar = () => {
    dispatch({
      type: 'onDemandFilter/reset',
    })
    dispatch(clearNewOnDemandSegment())
    navigate(`/institution/${ipedId}/prospects/contracts`)
    setShowSidebar()
  }

  /** If segmentId is set in the url or segment action changes,
   *  show the sidebar, change labels and fetch segment information
   */
  useEffect(() => {
    if (segmentId) {
      setShowSidebar()
      if (segmentAction === SegmentAction.edit) {
        setSidebarTitle(SegmentAction.edit)
      }
      if (segmentAction === SegmentAction.duplicate) {
        setSidebarTitle(SegmentAction.duplicate)
      }
      fetchSegment({
        ipedId,
        segmentId,
      })
    } else {
      setSidebarTitle(SegmentAction.new)
    }
  }, [segmentId, segmentAction])

  useEffect(() => {
    if (segmentData) {
      fetchContract({
        ipedId,
        contractId: segmentData?.['data'].contract_id,
      })
    }
  }, [segmentData])

  if (isFetching) return <Loading />

  return (
    <Body>
      <BodyHeader>
        <BodyTitle>Active Contracts</BodyTitle>
        <BodyActions>
          {canCreateContract && (
            <DefaultButton
              onClick={() =>
                navigate(`/institution/${ipedId}/prospects/contracts/new`)
              }
            >
              Add Contract
            </DefaultButton>
          )}
        </BodyActions>
      </BodyHeader>
      <ContractsContainer
        contractSource={ContractSource.candidates}
        contractStatus={[ContractStatus.pending, ContractStatus.in_progress, ContractStatus.flagged]}
      />
      <BodySubHeader>
        <BodyTitle>Expired Contracts</BodyTitle>
      </BodySubHeader>
      <ContractsContainer
        contractSource={ContractSource.candidates}
        contractStatus={[ContractStatus.completed, ContractStatus.expired, ContractStatus.deleted, ContractStatus.paused]}
      />
      {canAddSegment && <Sidebar
        sidebarTitle={[SegmentAction.new]}
        header={<HeaderLabel label={sidebarTitle} />}
        showSidebar={showSidebar}
        setShowSideBar={setShowSidebar}
        handleCloseSidebar={handleCloseSidebar}
        setCurrentTitle={setSidebarTitle}
      >
        {sidebarTitle === SegmentAction.new && (
          <OnDemandFilter
            showSidebar={showSidebar}
            handleCloseSidebar={handleCloseSidebar}
          />
        )}
        {(sidebarTitle === SegmentAction.edit ||
          sidebarTitle === SegmentAction.duplicate) && (
          <OnDemandEditDuplicateFilter
            showSidebar={showSidebar}
            handleCloseSidebar={handleCloseSidebar}
            segmentAction={segmentAction}
            candidateContract={candidateContract}
            segmentData={segmentData?.['data']}
            segmentId={segmentId}
            isSegmentFetching={isSegmentFetching}
          />
        )}
        {sidebarTitle === SegmentAction.explore && (
          <OnDemandExploreSegmentsFilter />
        )}
      </Sidebar>}
    </Body>
  )
}
